import * as React from 'react';
import type {
	IOption,
	IUseSelectTagsProps,
	IUseSelectTagsResult,
	SelectOptionValue,
} from '../types';

export const useSelectTags = <TValue extends SelectOptionValue = SelectOptionValue, TMetadata = unknown>({
	maxSelections,
	onChange,
	options,
	value,
}: IUseSelectTagsProps<TValue, TMetadata>): IUseSelectTagsResult<TValue, TMetadata> => {
	const selectedOptions = React.useMemo(
		() => {
			const optionByValue = new Map<TValue, IOption<TValue, TMetadata>>(
				options.map(option => [option.value, option])
			);

			return value.map(
				(selectedValue): IOption<TValue, TMetadata> =>
					optionByValue.get(selectedValue) ?? {
						label: String(selectedValue),
						value: selectedValue,
					}
			);
		},
		[options, value]
	);

	const hasReachedMaxSelections = maxSelections !== undefined && value.length >= maxSelections;
	const canSelectMore = !hasReachedMaxSelections;

	const removeSelectedValue = React.useCallback(
		(selectedValue: TValue) => {
			if (!value.includes(selectedValue)) {
				return;
			}

			onChange(value.filter(valueItem => valueItem !== selectedValue));
		},
		[onChange, value]
	);

	return {
		canSelectMore,
		hasReachedMaxSelections,
		removeSelectedValue,
		selectedOptions,
	};
};
